import cn from 'classnames';
import type { Options } from '../../config/index.ts';
import type { ProductType } from '@/domain/Product.ts';
import FilterGroup from './parts/FilterGroup/FilterGroup.tsx';
import SortPicker from './parts/SortPicker/SortPicker.tsx';
import TechnologyPicker from './parts/TechnologyPicker/TechnologyPicker.tsx';
import styles from './FilterBar.module.scss';
import type { FiltersAndSort } from '@/widgets/services/FilteringAndSortingService.ts';
import AllFiltersDrawer from '../../drawers/AllFiltersDrawer/AllFiltersDrawer.tsx';
import TechnologyInfoDrawer from '../../drawers/TechnologyInfoDrawer/TechnologyInfoDrawer.tsx';
import useABTestVariant from '@/widgets/hooks/useABTestVariant.tsx';
import useWidgetOptions from '@/widgets/hooks/useWidgetOptions.tsx';
import EXPERIMENTS from '@/constants/abExperiments.ts';

export type FilterBarProps = {
  filtersAndSort: FiltersAndSort;
  productType: ProductType;
};

export default function FilterBar({
  filtersAndSort,
  productType
}: FilterBarProps) {
  // CSS top position controlled on ProductListLayout.module.scss
  const options = useWidgetOptions<Options>();
  const enableStickyFiltersVariant = useABTestVariant(
    EXPERIMENTS.STICKY_FILTERS.ENABLED
  );
  const hasTechnologySelector: ProductType[] = ['capsule', 'machine'];
  const showTechnologyPicker = hasTechnologySelector.includes(productType);

  // Note: Options is used to keep backward compatibility with these markets that are
  // using this flag. It should be removed it in the future.
  const enableStickyFilters =
    options.enableStickyFilters || enableStickyFiltersVariant;

  return (
    <>
      <nav
        class={cn(styles.container, {
          [styles.containerSticky]: enableStickyFilters
        })}
      >
        {showTechnologyPicker && <TechnologyPicker />}
        <FilterGroup filtersAndSort={filtersAndSort} />
        <SortPicker
          sorting={filtersAndSort.sorting}
          class={styles.sortPicker}
          id="productlist-sortpicker-1"
        />
      </nav>
      <AllFiltersDrawer filtersAndSort={filtersAndSort} />
      <TechnologyInfoDrawer />
    </>
  );
}

FilterBar.Skeleton = function Skeleton() {
  return (
    <nav class={styles.container}>
      <TechnologyPicker.Skeleton />
      <FilterGroup.Skeleton />
      <SortPicker.Skeleton />
    </nav>
  );
};
